import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import {
  getPortalRewards,
  getPortalRewardsTransactions,
} from "../../api/clientPortalPublic";
import AuthRequiredCard from "../../components/clientPortal/AuthRequiredCard";
import { useClientPortalAuth } from "../../context/ClientPortalAuthContext";
import { formatApiError } from "../../utils/formatApiError";

const LOCK_TITLE = "Rewards are available after your account is activated.";
const LOCK_BODY =
  "Rewards and Gift Cards will appear once the salon activates your access. If it's not activated within 48 hours, please contact the salon or ask at your appointment.";

const formatTxType = (value) => {
  if (!value) return "Activity";
  return String(value).replace(/_/g, " ");
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

export default function PortalRewards() {
  const { salonId } = useOutletContext();
  const { token, client, clientMe } = useClientPortalAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rewards, setRewards] = useState(null);
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    if (!token || client) return;
    clientMe().catch(() => {});
  }, [token, client, clientMe]);

  const isActivated = client?.portal_is_activated === true;
  const showLoginPrompt = !token;
  const showActivationLock = token && client && !isActivated;

  useEffect(() => {
    if (!token || !isActivated) return;
    let active = true;
    const loadRewards = async () => {
      setLoading(true);
      setError("");
      try {
        const [rewardsData, txData] = await Promise.all([
          getPortalRewards(token),
          getPortalRewardsTransactions(token, { limit: 50, offset: 0 }),
        ]);
        if (!active) return;
        setRewards(rewardsData || null);
        setTransactions(Array.isArray(txData) ? txData : []);
      } catch (err) {
        if (!active) return;
        setError(formatApiError(err));
        setRewards(null);
        setTransactions([]);
      } finally {
        if (active) setLoading(false);
      }
    };
    loadRewards();
    return () => {
      active = false;
    };
  }, [token, isActivated]);

  if (showLoginPrompt) {
    return (
      <AuthRequiredCard
        title="Sign in to view rewards"
        subtitle="Please log in or register to access your rewards."
        salonId={salonId}
      />
    );
  }

  if (showActivationLock) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center">
        <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
          {LOCK_TITLE}
        </h2>
        <p className="mt-2 text-sm md:text-base text-slate-600">{LOCK_BODY}</p>
        <div className="mt-5">
          <Link
            to={`/portal/salon/${salonId}/book`}
            className="rounded-full bg-slate-900 px-5 py-2 text-sm md:text-base font-semibold text-white hover:bg-slate-800"
          >
            Book an appointment
          </Link>
        </div>
      </div>
    );
  }

  if (token && !client) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-600">
        Loading your account...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div>
          <h2 className="text-xl md:text-2xl font-semibold text-slate-900">
            Rewards
          </h2>
          <p className="mt-1 text-sm md:text-base text-slate-600">
            Track your points and recent rewards activity.
          </p>
        </div>

        {error && (
          <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="mt-4 text-sm text-slate-500">Loading rewards...</div>
        ) : (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-xs text-slate-500">Points balance</div>
              <div className="text-lg font-semibold text-slate-900">
                {rewards?.points_balance ?? 0} pts
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-xs text-slate-500">Available now</div>
              <div className="text-lg font-semibold text-slate-900">
                {rewards?.available_points_balance ?? 0} pts
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-xs text-slate-500">Pending</div>
              <div className="text-lg font-semibold text-slate-900">
                {rewards?.pending_points_balance ?? 0} pts
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-xs text-slate-500">Lifetime earned</div>
              <div className="text-lg font-semibold text-slate-900">
                {rewards?.lifetime_earned_points ?? 0} pts
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-xs text-slate-500">Lifetime redeemed</div>
              <div className="text-lg font-semibold text-slate-900">
                {rewards?.lifetime_redeemed_points ?? 0} pts
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="text-xs text-slate-500">Referral code</div>
              <div className="text-lg font-semibold text-slate-900">
                {rewards?.referral_code || "--"}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              Rewards history
            </h3>
            <p className="text-sm text-slate-600">
              Recent rewards activity for your account.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="mt-4 text-sm text-slate-500">Loading history...</div>
        ) : transactions.length === 0 ? (
          <div className="mt-4 text-sm text-slate-500">
            No rewards activity yet.
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {transactions.map((tx) => (
              <div
                key={tx.id}
                className="rounded-2xl border border-slate-200 px-4 py-3 text-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-semibold text-slate-900">
                      {formatTxType(tx.tx_type)}
                    </div>
                    <div className="text-xs text-slate-500">
                      {formatDateTime(tx.posted_at)}
                    </div>
                    {tx.note && (
                      <div className="mt-1 text-xs text-slate-600">
                        {tx.note}
                      </div>
                    )}
                  </div>
                  <div
                    className={`text-sm font-semibold ${
                      tx.points_delta >= 0 ? "text-emerald-600" : "text-rose-600"
                    }`}
                  >
                    {tx.points_delta >= 0 ? "+" : ""}
                    {tx.points_delta} pts
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
